import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Save, Trash2, X as XIcon, Film, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { FilmPhotoUploader, FilmPhotoGallery } from "./film/FilmPhotos";

const EMPTY = {
  id: null,
  date: new Date().toISOString().slice(0, 10),
  title: "",
  url: "",
  notes: "",
  photos: [],
};

export default function FilmReviews({ playerId, playerName }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listFilmReviews(playerId);
      setRows(data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [playerId]);

  const startNew = () => setDraft({ ...EMPTY });
  const cancelNew = () => setDraft(null);
  const saveNew = async () => {
    if (!draft.title.trim()) { toast.error("Title required"); return; }
    if (!draft.date) { toast.error("Date required"); return; }
    try {
      const payload = { date: draft.date, title: draft.title,
                        url: draft.url || null, notes: draft.notes || null, photos: draft.photos || [] };
      await api.createFilmReview(playerId, payload);
      toast.success("Film review added");
      setDraft(null);
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const startEdit = (row) => { setEditingId(row.id); setEditingDraft({ ...EMPTY, ...row }); };
  const cancelEdit = () => { setEditingId(null); setEditingDraft(null); };
  const saveEdit = async () => {
    if (!editingDraft.title.trim()) { toast.error("Title required"); return; }
    try {
      const payload = { date: editingDraft.date, title: editingDraft.title,
                        url: editingDraft.url || null, notes: editingDraft.notes || null, photos: editingDraft.photos || [] };
      await api.updateFilmReview(playerId, editingId, payload);
      toast.success("Updated");
      cancelEdit();
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete film review "${row.title}"?`)) return;
    try {
      await api.deleteFilmReview(playerId, row.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <section className="border border-white/10 bg-[#101010]" data-testid="film-reviews">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Film className="w-4 h-4 text-[var(--org-accent)]" />
          <h3 className="font-heading uppercase text-lg tracking-[0.2em]">Film Reviews</h3>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">
            {rows.length} {rows.length === 1 ? "clip" : "clips"}
          </span>
        </div>
        {canEdit && !draft && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]"
            data-testid="film-add-btn"
          >
            <Plus className="w-3 h-3" /> Add Film
          </button>
        )}
      </div>

      {draft && <FilmForm draft={draft} setDraft={setDraft} onSave={saveNew} onCancel={cancelNew} testPrefix="new" playerId={playerId} />}

      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2 text-[#a0a0a0]">
          <Loader2 className="w-4 h-4 animate-spin" /> loading…
        </div>
      ) : rows.length === 0 && !draft ? (
        <div className="text-center py-12 text-[#757575] text-sm">
          No film reviews yet. {canEdit && "Add the first one above."}
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {rows.map(r => (
            editingId === r.id ? (
              <FilmForm
                key={r.id} draft={editingDraft} setDraft={setEditingDraft}
                onSave={saveEdit} onCancel={cancelEdit} testPrefix={`edit-${r.id}`} playerId={playerId}
              />
            ) : (
              <FilmRow
                key={r.id} row={r} playerId={playerId}
                canEdit={canEdit}
                onEdit={() => startEdit(r)}
                onDelete={() => remove(r)}
              />
            )
          ))}
        </div>
      )}
    </section>
  );
}

function FilmRow({ row, playerId, canEdit, onEdit, onDelete }) {
  return (
    <div className="px-6 py-5" data-testid={`film-row-${row.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--org-accent)]">
              {row.date}
            </div>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="font-heading text-xl text-white uppercase tracking-tight">{row.title}</div>
            {row.url && (
              <a
                href={row.url} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-[var(--org-accent)] hover:underline"
                data-testid={`film-link-${row.id}`}
              >
                <ExternalLink className="w-3 h-3" /> Open Film
              </a>
            )}
          </div>
          {row.url && (
            <div className="text-[11px] text-[#757575] font-mono truncate mb-2">{row.url}</div>
          )}
          {row.notes && (
            <div className="text-sm text-[#d8d8d8] whitespace-pre-wrap leading-relaxed">{row.notes}</div>
          )}
          <FilmPhotoGallery playerId={playerId} photos={row.photos} rowId={row.id} />
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onEdit} title="Edit"
              className="p-2 border border-white/10 text-[#a0a0a0] hover:text-white hover:border-white/30"
              data-testid={`film-edit-${row.id}`}
            ><Pencil className="w-3 h-3" /></button>
            <button
              onClick={onDelete} title="Delete"
              className="p-2 border border-white/10 text-[#a0a0a0] hover:text-red-300 hover:border-red-500/40"
              data-testid={`film-delete-${row.id}`}
            ><Trash2 className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilmForm({ draft, setDraft, onSave, onCancel, testPrefix, playerId }) {
  const upd = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <div className="px-6 py-5 bg-[#141414] border-b border-white/10" data-testid={`film-form-${testPrefix}`}>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Date *</div>
          <input
            type="date" value={draft.date} onChange={e => upd("date", e.target.value)}
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`film-input-date-${testPrefix}`}
          />
        </div>
        <div className="col-span-9">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Title / Context *</div>
          <input
            value={draft.title} onChange={e => upd("title", e.target.value)}
            placeholder="Game vs St. Joseph 2/12, Skill Session, etc."
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`film-input-title-${testPrefix}`}
          />
        </div>
        <div className="col-span-12">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Film Link (Hudl / YouTube / Vimeo)</div>
          <input
            value={draft.url || ""} onChange={e => upd("url", e.target.value)}
            placeholder="https://www.hudl.com/video/…"
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`film-input-url-${testPrefix}`}
          />
        </div>
        <div className="col-span-12">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Notes / Coaching Feedback</div>
          <textarea
            value={draft.notes || ""} onChange={e => upd("notes", e.target.value)}
            rows={5} placeholder="What did we see? Wins, losses, next reps to build on…"
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`film-input-notes-${testPrefix}`}
          />
        </div>
        <div className="col-span-12">
          <FilmPhotoUploader playerId={playerId} photos={draft.photos} testPrefix={testPrefix}
            onChange={fn => setDraft(d => ({ ...d, photos: typeof fn === "function" ? fn(d.photos || []) : fn }))} />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white"
          data-testid={`film-form-cancel-${testPrefix}`}
        ><XIcon className="w-3 h-3" /> Cancel</button>
        <button
          onClick={onSave}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]"
          data-testid={`film-form-save-${testPrefix}`}
        ><Save className="w-3 h-3" /> Save</button>
      </div>
    </div>
  );
}
